import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api/client";
import type { AppSettings, PracticeSchedule, Session, Usage } from "../types";
import { buildPracticeICS, downloadICS } from "../lib/ics";
import MonthCalendar from "../components/calendar/MonthCalendar";
import ScheduleEditor from "../components/calendar/ScheduleEditor";
import GenerationLoader from "../components/GenerationLoader";

const DEFAULT_SCHEDULE: PracticeSchedule = { enabled: false, days: [], time: "18:00", reminders: true, reminder_minutes_before: 0 };
const WEEKDAY_FULL = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const dayKey = (d: Date) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
const mondayIndex = (d: Date) => (d.getDay() + 6) % 7;

/** The plan is active exactly when at least one day is chosen — the day picker is
 *  the single source of truth, so `enabled` always mirrors it. */
const normalizeSchedule = (s: PracticeSchedule): PracticeSchedule => ({ ...s, enabled: s.days.length > 0 });

function formatDuration(s: number | null): string {
  if (!s) return "—";
  const m = Math.round(s / 60);
  return m < 1 ? "<1m" : `${m}m`;
}

function formatPracticeTime(totalSeconds: number): string {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.round((totalSeconds % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

/** Next `count` dates (from `from`, inclusive) whose weekday is in `days`. */
function nextOccurrences(days: number[], count: number, from: Date): Date[] {
  if (days.length === 0) return [];
  const out: Date[] = [];
  for (let i = 0; i < 90 && out.length < count; i++) {
    const d = new Date(from.getFullYear(), from.getMonth(), from.getDate() + i);
    if (days.includes(mondayIndex(d))) out.push(d);
  }
  return out;
}

function StatTile({ value, suffix, label, className }: { value: string | number; suffix?: string | number; label: string; className?: string }) {
  return (
    <div className={`card px-5 py-4 ${className ?? ""}`}>
      <div className="font-display font-semibold tracking-[-0.02em] text-[1.75rem] text-ink leading-tight tabular-nums">
        {value}
        {suffix != null && <span className="text-[1.1rem] text-text-tertiary font-normal"> / {suffix}</span>}
      </div>
      <div className="section-label mt-1">{label}</div>
    </div>
  );
}

export default function PracticePlanPage() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState<AppSettings | null>(null);
  const [schedule, setSchedule] = useState<PracticeSchedule>(DEFAULT_SCHEDULE);
  const [saved, setSaved] = useState<PracticeSchedule>(DEFAULT_SCHEDULE);
  const [sessions, setSessions] = useState<Session[] | null>(null);
  const [usage, setUsage] = useState<Usage | null>(null);
  const [month, setMonth] = useState(() => new Date());
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<{ type: "success" | "error"; msg: string } | null>(null);

  useEffect(() => {
    let alive = true;
    api.getSettings().then((s) => {
      if (!alive) return;
      setSettings(s);
      const sched = normalizeSchedule(s.practice_schedule ?? DEFAULT_SCHEDULE);
      setSchedule(sched);
      setSaved(sched);
    }).catch(() => {});
    api.getSessions(365).then((s) => { if (alive) setSessions(s); }).catch(() => { if (alive) setSessions([]); });
    api.getUsage().then((u) => { if (alive) setUsage(u); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const today = new Date();

  const { practicedDays, sessionsByDay } = useMemo(() => {
    const set = new Set<string>();
    const byDay = new Map<string, Session[]>();
    for (const s of sessions ?? []) {
      if (!s.ended_at) continue;
      const k = dayKey(new Date(s.started_at));
      set.add(k);
      const arr = byDay.get(k);
      if (arr) arr.push(s);
      else byDay.set(k, [s]);
    }
    return { practicedDays: set, sessionsByDay: byDay };
  }, [sessions]);

  // KPIs track the month the user is viewing, not always the current one.
  const { monthCount, monthSeconds } = useMemo(() => {
    let count = 0;
    let seconds = 0;
    for (const s of sessions ?? []) {
      if (!s.ended_at) continue;
      const d = new Date(s.started_at);
      if (d.getFullYear() === month.getFullYear() && d.getMonth() === month.getMonth()) {
        count++;
        seconds += s.duration_s ?? 0;
      }
    }
    return { monthCount: count, monthSeconds: seconds };
  }, [sessions, month]);

  const isCurrentMonth = month.getFullYear() === today.getFullYear() && month.getMonth() === today.getMonth();
  const monthLabel = month.toLocaleDateString(undefined, { month: "long" });

  const dirty = JSON.stringify(schedule) !== JSON.stringify(saved);
  const upcoming = schedule.days.length > 0 ? nextOccurrences(schedule.days, 5, today) : [];

  const updateSchedule = (next: PracticeSchedule) => setSchedule(normalizeSchedule(next));

  const save = async () => {
    if (!settings || saving) return;
    setSaving(true);
    setStatus(null);
    try {
      const next = await api.updateSettings({ ...settings, practice_schedule: schedule });
      setSettings(next);
      const sched = normalizeSchedule(next.practice_schedule ?? schedule);
      setSchedule(sched);
      setSaved(sched);
      setStatus({ type: "success", msg: "Plan saved." });
    } catch {
      setStatus({ type: "error", msg: "Couldn't save your plan. Try again." });
    } finally {
      setSaving(false);
    }
  };

  const exportICS = () => {
    const ics = buildPracticeICS(schedule);
    if (!ics) {
      setStatus({ type: "error", msg: "Pick at least one practice day first." });
      return;
    }
    downloadICS("ciaotutor-practice", ics);
    setStatus({ type: "success", msg: "Calendar file downloaded. Open it to add your reminders." });
  };

  if (!sessions || !settings) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <GenerationLoader messages={["Loading your calendar...", "Gathering your sessions..."]} subtitle="" />
      </div>
    );
  }

  const selectedSessions = selectedKey ? sessionsByDay.get(selectedKey) ?? [] : [];

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="max-w-6xl mx-auto w-full px-5 sm:px-8 py-10">
        <header className="mb-8 animate-rise">
          <h1>Calendar</h1>
          <p className="text-text-tertiary text-[0.9375rem] mt-1">Your practice history, and a gentle plan for the days ahead.</p>
        </header>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4 mb-8">
          <StatTile value={monthCount} suffix={isCurrentMonth && usage ? usage.limit : undefined} label={isCurrentMonth ? "Sessions this month" : `Sessions in ${monthLabel}`} />
          <StatTile value={formatPracticeTime(monthSeconds)} label="Practice time" />
          <StatTile value={schedule.days.length} label="Planned days / week" className="hidden sm:block" />
        </div>

        {status && (
          <div className={`mb-6 px-4 py-2.5 rounded-[10px] text-[0.85rem] ${
            status.type === "success" ? "bg-success/15 text-success" : "bg-rose-500/15 text-rose-400"
          }`}>
            {status.msg}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_330px] gap-6 items-start">
          <div className="card p-6 sm:p-7 animate-rise">
            <MonthCalendar
              month={month}
              practicedDays={practicedDays}
              schedule={schedule}
              today={today}
              onPrev={() => setMonth((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1))}
              onNext={() => setMonth((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1))}
              onSelectDay={(d) => setSelectedKey((cur) => (cur === dayKey(d) ? null : dayKey(d)))}
              selectedKey={selectedKey}
            />

            {selectedKey && selectedSessions.length > 0 && (
              <div className="mt-6 pt-5 border-t border-border-soft">
                <h4 className="section-label mb-3">
                  {new Date(selectedSessions[0].started_at).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
                </h4>
                <div className="flex flex-col gap-2">
                  {selectedSessions.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => navigate(`/dashboard?session=${s.id}`)}
                      className="flex items-center justify-between gap-3 text-left px-3 py-2.5 rounded-[10px] bg-surface2/40 hover:bg-surface2 transition-colors cursor-pointer"
                    >
                      <span className="min-w-0">
                        <span className="block text-[0.875rem] text-ink truncate">{s.topic || "Conversation"}</span>
                        <span className="block text-[0.72rem] text-text-tertiary tabular-nums">
                          {new Date(s.started_at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })} · {formatDuration(s.duration_s)}
                        </span>
                      </span>
                      <span className="text-text-tertiary text-[0.72rem] flex-shrink-0">View →</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="card p-6 animate-rise [animation-delay:0.05s]">
            <ScheduleEditor value={schedule} onChange={updateSchedule} onSave={save} onExport={exportICS} saving={saving} dirty={dirty} />
          </div>
        </div>

        {upcoming.length > 0 && (
          <div className="card p-6 mt-6 animate-rise [animation-delay:0.1s]">
            <h4 className="section-label mb-4">Coming up</h4>
            <div className="flex flex-col divide-y divide-border-soft">
              {upcoming.map((d, i) => (
                <div key={i} className="flex items-center justify-between py-2.5 first:pt-0 last:pb-0">
                  <span className="text-[0.875rem] text-text">
                    {WEEKDAY_FULL[mondayIndex(d)]}
                    <span className="text-text-tertiary"> · {d.toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>
                  </span>
                  <span className="text-[0.8rem] text-text-tertiary tabular-nums">{schedule.time}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
